import { useMemo } from 'react';

import { TagInput, type TagValidationResult } from '@/shared/ui/tag-input';
import { useToast } from '@/stores/pure/ToastStore';

type Props = {
  values: FilterKeyValue[];
  onChange: (values: FilterKeyValue[]) => void;
};

const normalizeKeyword = (value: string): string | null => {
  const trimmed = value.replace(/^#+/, '').trim();
  return trimmed ? trimmed : null;
};

export const StructuredKeywordFilterInput = ({
  values,
  onChange,
}: Props) => {
  const addToast = useToast();

  const displayValues = useMemo(
    () =>
      values
        .map((value) => normalizeKeyword(String(value.filterKey)))
        .filter((value): value is string => !!value),
    [values],
  );

  const validateTag = (
    tag: string,
    currentTags: string[],
  ): TagValidationResult => {
    const normalized = normalizeKeyword(tag);
    if (!normalized) return 'invalid';

    const hasDuplicate = currentTags
      .map((value) => normalizeKeyword(value))
      .filter((value): value is string => !!value)
      .includes(normalized);

    if (hasDuplicate) return 'duplicate';
    return null;
  };

  const transformOnAdd = (tag: string) => {
    return normalizeKeyword(tag);
  };

  const onValidationError = (result: TagValidationResult) => {
    if (result === 'duplicate') {
      addToast('This keyword has already been added', 'fail');
      return;
    }

    if (result === 'invalid') {
      addToast('Keyword cannot be empty', 'fail');
    }
  };

  const handleChange = (nextValues: string[]) => {
    onChange(
      nextValues.map((value) => ({
        filterKey: value,
        filterName: value,
      })),
    );
  };

  return (
    <div className="w-100 p-2">
      <TagInput
        value={displayValues}
        onChange={handleChange}
        placeholder="Enter keyword"
        validateTag={validateTag}
        transformOnAdd={transformOnAdd}
        onValidationError={onValidationError}
        inputFilter={() => true}
      />
    </div>
  );
};
